import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Tooltip,
  Checkbox,
} from "@mui/material";
import {
  collection,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
  doc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import ThumbUpAlt from "@mui/icons-material/ThumbUpAlt";

const pad2 = (n) => String(n).padStart(2, "0");
const toYMD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromYMD = (s) => {
  const [y, m, d] = s.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
};

const DOW_INITIAL = ["S","M","T","W","T","F","S"]; // 0..6 = Sun..Sat
const enumerateDates = (start, end) => {
  const out = [];
  const d = new Date(start); d.setHours(0,0,0,0);
  const e = new Date(end);   e.setHours(0,0,0,0);
  while (d <= e) { out.push(toYMD(d)); d.setDate(d.getDate() + 1); }
  return out;
};

// chunk helper for Firestore `in` queries (max 10 IDs per query)
const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function LoginActivity() {
  const auth = getAuth();
  const db = getFirestore();

  const today = useMemo(() => new Date(), []);
  const monthStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today]
  );
  const monthEnd = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + 1, 0),
    [today]
  );

  const [startDate, setStartDate] = useState(toYMD(monthStart));
  const [endDate, setEndDate] = useState(toYMD(monthEnd));

  const [allUsers, setAllUsers] = useState([]);       // [{uid,label,email,name}]
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dataMap, setDataMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingGrid, setLoadingGrid] = useState(false);

  // role + company for current user
  const [roleFromDb, setRoleFromDb] = useState("");
  const [companyId, setCompanyId] = useState("");

  const role = (roleFromDb || "").trim();
  const isAdmin = role.toLowerCase() === "admin";
  const isManager = role.toLowerCase() === "manager";
  const isMember = !isAdmin && !isManager;

  // Select-all option
  const ALL_OPTION = useMemo(
    () => ({ uid: "__ALL__", label: `Select all (${allUsers.length})` }),
    [allUsers.length]
  );
  const allSelected =
    selectedUsers.length > 0 && selectedUsers.length === allUsers.length;

  // Load current user's role/company
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        if (snap.exists()) {
          const data = snap.data() || {};
          setRoleFromDb(data.role || "");
          setCompanyId(data.companyId || "");
        }
      } finally {
        // noop
      }
    })();
  }, [auth, db]);

  // Helper: fetch users by IDs (batched `in` queries)
  const fetchUsersByIds = async (uids) => {
    if (!uids?.length) return [];
    const results = [];
    for (const group of chunk(uids, 10)) {
      const qRef = query(
        collection(db, "users"),
        where(documentId(), "in", group)
      );
      const snap = await getDocs(qRef);
      results.push(
        ...snap.docs.map((d) => {
          const v = d.data() || {};
          const label = v.displayName || v.name || v.email || d.id;
          return {
            uid: d.id,
            label,
            email: v.email || "",
            name: v.displayName || v.name || "",
          };
        })
      );
    }
    return results;
  };

  // Helper: load all company users (Admin)
  const fetchAllCompanyUsers = async (cid) => {
    const qRef = query(collection(db, "users"), where("companyId", "==", cid));
    const snap = await getDocs(qRef);
    return snap.docs
      .map((d) => {
        const v = d.data() || {};
        const label = v.displayName || v.name || v.email || d.id;
        return {
          uid: d.id,
          label,
          email: v.email || "",
          name: v.displayName || v.name || "",
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Helper: manager → memberIds from managerMemberLinks
  const fetchManagerMemberIds = async (cid, managerId) => {
    const qRef = query(
      collection(db, "managerMemberLinks"),
      where("companyId", "==", cid),
      where("managerId", "==", managerId)
    );
    const snap = await getDocs(qRef);
    const ids = new Set();
    snap.forEach((d) => {
      const v = d.data() || {};
      if (v.memberId) ids.add(String(v.memberId));
    });
    return Array.from(ids);
  };

  // Load users list based on role (Admin: all, Manager: members+me, Member: me)
  useEffect(() => {
    (async () => {
      try {
        const me = auth.currentUser;
        if (!me || !companyId) return;

        if (isAdmin) {
          const list = await fetchAllCompanyUsers(companyId);
          setAllUsers(list);
          setSelectedUsers(list); // preselect all for admins
        } else if (isManager) {
          const memberIds = await fetchManagerMemberIds(companyId, me.uid);
          // include manager self
          const ids = Array.from(new Set([me.uid, ...memberIds]));
          const list = await fetchUsersByIds(ids);
          list.sort((a, b) => a.label.localeCompare(b.label));
          setAllUsers(list);
          setSelectedUsers(list); // preselect all allowed users for manager
        } else {
          // Member = only self
          const snap = await getDoc(doc(db, "users", me.uid));
          const v = snap.exists() ? snap.data() || {} : {};
          const label = v.displayName || v.name || v.email || me.email || me.uid;
          const meRow = {
            uid: me.uid,
            label,
            email: v.email || me.email || "",
            name: v.displayName || v.name || "",
          };
          setAllUsers([meRow]);
          setSelectedUsers([meRow]);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [auth, db, companyId, isAdmin, isManager]);

  // Load grid data for each selected user
  const reloadGrid = async () => {
    if (!startDate || !endDate) return;
    const start = fromYMD(startDate);
    const end = fromYMD(endDate);
    if (isNaN(start) || isNaN(end) || start > end) return;

    setLoadingGrid(true);
    try {
      const map = new Map();
      for (const u of selectedUsers) {
        const qRef = query(
          collection(db, "users", u.uid, "loginDays"),
          where(documentId(), ">=", startDate),
          where(documentId(), "<=", endDate)
        );
        const snap = await getDocs(qRef);
        const set = new Set();
        snap.forEach((d) => set.add(d.id));
        map.set(u.uid, set);
      }
      setDataMap(map);
    } catch (e) {
      console.error("Failed to load login activity:", e);
      setDataMap(new Map());
    } finally {
      setLoadingGrid(false);
    }
  };

  useEffect(() => {
    reloadGrid();
    
  }, [startDate, endDate, selectedUsers]);

  const dateCols = useMemo(
    () => enumerateDates(fromYMD(startDate), fromYMD(endDate)),
    [startDate, endDate]
  );

  const displayUsers = useMemo(() => {
    if (selectedUsers.length > 0) return selectedUsers;
    const byId = new Map(allUsers.map((u) => [u.uid, u]));
    return [...dataMap.keys()].map((uid) => byId.get(uid) || { uid, label: uid });
  }, [selectedUsers, dataMap, allUsers]);

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 3 }} sx={{ fontFamily: (theme) => theme.typography.fontFamily }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
        Login Activity
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        {/* Row 1: Labels */}
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={1}>
          <Box sx={{ width: 150 }}>
            <Typography variant="caption">Start date</Typography>
          </Box>
          <Box sx={{ width: 150 }}>
            <Typography variant="caption">End date</Typography>
          </Box>
          <Box sx={{ minWidth: 320, flex: 1 }}>
            <Typography variant="caption">Users</Typography>
          </Box>
          <Box sx={{ width: 100 }} />
        </Box>

        {/* Row 2: Inputs */}
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            size="small"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            inputProps={{ max: endDate }}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{ min: startDate }}
            sx={{ width: 150 }}
          />

          {isAdmin || isManager ? (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={[ALL_OPTION, ...allUsers]}
              getOptionLabel={(o) => o.label}
              value={selectedUsers}
              onChange={(_, val, _reason, details) => {
                const clicked = details?.option;
                if (clicked?.uid === "__ALL__") {
                  setSelectedUsers(allSelected ? [] : allUsers);
                  return;
                }
                setSelectedUsers(val.filter((u) => u.uid !== "__ALL__"));
              }}
              renderOption={(props, option, { selected }) => {
                const isAll = option.uid === "__ALL__";
                const checked = isAll ? allSelected : selected;
                const indeterminate = isAll && selectedUsers.length > 0 && !allSelected;
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </li>
                );
              }}
              size="small"
              limitTags={2}
              renderTags={(value, getTagProps) => {
                const shown = value.slice(0, 2);
                const hidden = value.length - shown.length;
                return [
                  ...shown.map((option, index) => (
                    <Chip
                      key={option.uid}
                      size="small"
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                    />
                  )),
                  hidden > 0 && <Chip key="more" size="small" label={`+${hidden} more`} />,
                ];
              }}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Filter users (optional)" />
              )}
              sx={{
                minWidth: 320,
                flex: 1,
                "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap" },
                "& .MuiAutocomplete-tag": { maxWidth: 160 },
              }}
            />
          ) : (
            <TextField
              size="small"
              value={selectedUsers[0]?.label || ""}
              InputProps={{ readOnly: true }}
              sx={{ minWidth: 320, flex: 1 }}
            />
          )}

          <Button variant="outlined" onClick={reloadGrid} disabled={loadingGrid}>
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Table */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "auto",
          pb: 2,
          maxHeight: { xs: "calc(100vh - 300px)", md: 560 }, // scroll container so sticky works
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `240px repeat(${dateCols.length}, minmax(20px, 1fr))`,
            position: "sticky",
            top: 0,
            zIndex: 5,
            bgcolor: "background.paper",
            borderBottom: "1px solid #eee",
            boxShadow: "0 2px 6px rgba(0,0,0,.04)",
          }}
        >
          {/* First column header cell also sticky to the left */}
          <Box
            sx={{
              p: 1.5,
              fontWeight: 600,
              position: "sticky",
              left: 0,
              zIndex: 6,
              bgcolor: "background.paper",
              borderRight: "1px solid #eee",
            }}
          >
            <Typography variant="subtitle2">User</Typography>
          </Box>

          {dateCols.map((d) => {
            const dayIdx = fromYMD(d).getDay(); // 0=Sun..6=Sat
            const dow = DOW_INITIAL[dayIdx];
            const isWeekend = dayIdx === 0 || dayIdx === 6;
            return (
              <Box
                key={d}
                sx={{
                  p: 1,
                  textAlign: "center",
                  borderRight: "1px solid #f2f2f2",
                  lineHeight: 1.1,
                }}
                title={`${d} (${dow})`}
              >
                <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
                  {d.slice(8)} {/* 01..31 */}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    lineHeight: 1,
                    mt: 0.25,
                    display: "inline-block",
                    px: 0.5,
                    borderRadius: 1,
                    bgcolor: isWeekend ? "#cdc9c9ff" : "transparent",
                    color: isWeekend ? "text.primary" : "text.secondary",
                  }}
                >
                  {dow}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Rows */}
        {loadingGrid ? (
          <Box p={3} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : displayUsers.length === 0 ? (
          <Box p={3} color="text.secondary">
            No users to display for the selected range.
          </Box>
        ) : (
          displayUsers.map((u) => {
            const set = dataMap.get(u.uid) || new Set();
            return (
              <Box
                key={u.uid}
                sx={{
                  display: "grid",
                  gridTemplateColumns: `240px repeat(${dateCols.length}, minmax(20px, 1fr))`,
                  borderBottom: "1px solid #f5f5f5",
                }}
              >
                {/* first body column sticky to the left */}
                <Box
                  sx={{
                    p: 1.25,
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    borderRight: "1px solid #eee",
                  }}
                  title={u.label}
                >
                  <Typography variant="body2">{u.label}</Typography>
                </Box>

                {dateCols.map((d) => {
                  const hit = set.has(d);
                  return (
                    <Tooltip key={`${u.uid}_${d}`} title={hit ? `Logged in on ${d}` : d}>
                      <Box
                        sx={{
                          m: 0.5,
                          height: 28,
                          borderRadius: 1.5,
                          border: hit ? "none" : "1px dashed #e5e7eb",
                          bgcolor: hit ? "#4caf50" : "#fafafa",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: hit ? "0 2px 8px rgba(76,175,80,.25)" : "none",
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {hit ? <ThumbUpAlt sx={{ fontSize: 18 }} /> : null}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })
        )}
      </Paper>

      <Box mt={1} color="text.secondary" fontSize={12}>
        Tip: Use the user filter to limit rows. Scroll horizontally to see all days in the chosen range.
      </Box>
    </Box>
  );
}
